import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { isNil } from 'lodash';
import { type ExpressionSpecification } from 'mapbox-gl';
import { useContext, useEffect, useMemo } from 'react';

import { useScoutLatestData } from '@/api/scout';
import {
    GEOFENCE_LINE_WIDTH,
    addOrUpdateGeoJSONLayer,
    createCircleGeometry,
    removeGeoJSONLayer,
} from '@/components/map/layers/geofenceUtils';
import { useTetherDeviceIdentity } from '@/hooks/useTetherDeviceIdentity';
import { mapInfoBlue, mapLabelFontStack } from '@/theme';
import { calculateDeviceLocation, lngLatToTurf, spotterToLngLat } from '@/utils';
import { getScoutDisplayWaypoints } from '@/utils/scoutStatusHelpers';

export type ScoutWaypointsLayerProps = {
    beforeLayer?: string;
    spotterId?: string | null;
};

const NEXT_WAYPOINT_FILL_OPACITY = 0.27;
const FUTURE_WAYPOINT_FILL_OPACITY = 0.1;
const WAYPOINT_LINE_COLOR = mapInfoBlue;
const WAYPOINT_FILL_COLOR = mapInfoBlue;
const WAYPOINT_CENTER_MARKER_COLOR = mapInfoBlue;
const WAYPOINT_LABEL_COLOR = '#FFFFFF';

const WAYPOINT_DOTTED_LINE_LAYOUT = {
    'line-join': 'round' as const,
    'line-cap': 'round' as const,
};

const WAYPOINT_DOTTED_LINE_PAINT = {
    'line-width': 1.25,
    'line-dasharray': [1, 2] as [number, number],
};

const WAYPOINT_CENTER_MARKER_RADIUS_STOPS: Array<[number, number]> = [
    [4, 8],
    [6, 7],
    [8, 6.5],
    [10, 10],
    [12, 9],
    [14, 10],
    [16, 14],
];

const WAYPOINT_LABEL_TEXT_SIZE_STOPS: Array<[number, number]> = [
    [4, 5],
    [8, 5],
    [10, 10],
    [12, 12],
    [14, 13],
    [16, 14],
];

/* Mapbox interpolate expression by zoom.
The radius values were chosen to keep the waypoints visible and appropriately sized across zoom levels, but can be adjusted as needed.
These are pairs of zoom level and radius values all in one array
zoom 4: radius 8px
zoom 6: radius 7px
etc
zoom 16: radius 14px
 */
const WAYPOINT_CENTER_MARKER_RADIUS_BY_ZOOM: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['zoom'],
    ...WAYPOINT_CENTER_MARKER_RADIUS_STOPS.flat(),
];

// Mapbox interpolate expression by zoom
const WAYPOINT_LABEL_TEXT_SIZE_BY_ZOOM: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['zoom'],
    ...WAYPOINT_LABEL_TEXT_SIZE_STOPS.flat(),
];

const WAYPOINTS_DOTTED_LINE_COLOR = '#7292A2';
const NEXT_WAYPOINT_DOTTED_LINE_COLOR = '#141414';

const SCOUT_POSITION_LINE_SOURCE = 'scout-position-line-source';
const SCOUT_POSITION_LINE_LAYER = 'scout-position-line';
const WAYPOINTS_LINE_SOURCE = 'scout-waypoints-line-source';
const WAYPOINTS_LINE_LAYER = 'scout-waypoints-line';

export const ScoutWaypointsLayer = ({ beforeLayer, spotterId: spotterIdProp }: ScoutWaypointsLayerProps) => {
    const { isScout, spotterId, device } = useTetherDeviceIdentity({ spotterId: spotterIdProp });
    const { data: scoutData } = useScoutLatestData({
        spotterId,
        config: { enabled: isScout },
    });
    const { map } = useContext(MapboxContext);
    const deviceLocation = device?.track?.[0];

    const remainingWaypoints = useMemo(() => getScoutDisplayWaypoints(scoutData), [scoutData]);

    const cleanupAllLayers = (mapInstance: mapboxgl.Map, waypointLayerIds: string[]) => {
        for (const id of waypointLayerIds) {
            removeGeoJSONLayer(mapInstance, id, id);
        }
        removeGeoJSONLayer(mapInstance, WAYPOINTS_LINE_LAYER, WAYPOINTS_LINE_SOURCE);
        removeGeoJSONLayer(mapInstance, SCOUT_POSITION_LINE_LAYER, SCOUT_POSITION_LINE_SOURCE);
    };

    useEffect(() => {
        if (!map || !isScout || !scoutData || remainingWaypoints.length === 0) {
            return;
        }

        // Render waypoint circles, labels, and connecting lines
        const layerIds: string[] = [];

        // --- Dotted line connecting all remaining waypoints ---
        if (remainingWaypoints.length >= 2) {
            const waypointCoords = remainingWaypoints.map(lngLatToTurf);
            const waypointsLineGeoJSON: GeoJSON.Feature<GeoJSON.LineString> = {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: waypointCoords,
                },
            };

            addOrUpdateGeoJSONLayer(
                map,
                { sourceId: WAYPOINTS_LINE_SOURCE, beforeId: beforeLayer },
                {
                    id: WAYPOINTS_LINE_LAYER,
                    type: 'line',
                    source: WAYPOINTS_LINE_SOURCE,
                    layout: WAYPOINT_DOTTED_LINE_LAYOUT,
                    paint: {
                        ...WAYPOINT_DOTTED_LINE_PAINT,
                        'line-color': WAYPOINTS_DOTTED_LINE_COLOR,
                    },
                },
                waypointsLineGeoJSON
            );
        }

        // --- Line from scout position to next waypoint ---

        const nextWp = remainingWaypoints[0];
        const reconciledScoutPosition = calculateDeviceLocation({
            spotterTrack: deviceLocation && [deviceLocation],
            scoutLocation: scoutData,
        });
        if (reconciledScoutPosition && nextWp) {
            const positionLineGeoJSON: GeoJSON.Feature<GeoJSON.LineString> = {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: [lngLatToTurf(reconciledScoutPosition), lngLatToTurf(nextWp)],
                },
            };
            addOrUpdateGeoJSONLayer(
                map,
                { sourceId: SCOUT_POSITION_LINE_SOURCE, beforeId: beforeLayer },
                {
                    id: SCOUT_POSITION_LINE_LAYER,
                    type: 'line',
                    source: SCOUT_POSITION_LINE_SOURCE,
                    layout: WAYPOINT_DOTTED_LINE_LAYOUT,
                    paint: {
                        ...WAYPOINT_DOTTED_LINE_PAINT,
                        'line-color': NEXT_WAYPOINT_DOTTED_LINE_COLOR,
                    },
                },
                positionLineGeoJSON
            );
        }

        // --- Waypoint circles and labels ---
        const waypointRadius = scoutData.mission?.waypoint_radius_m;
        if (!isNil(waypointRadius)) {
            remainingWaypoints.forEach((wp, index) => {
                const center = spotterToLngLat({ longitude: wp.longitude, latitude: wp.latitude });
                const circle = createCircleGeometry(center, waypointRadius);
                const isNext = index === 0;
                const fillOpacity = isNext ? NEXT_WAYPOINT_FILL_OPACITY : FUTURE_WAYPOINT_FILL_OPACITY;

                const fillId = `scout-wp-fill-${wp.sequence}`;
                const lineId = `scout-wp-line-${wp.sequence}`;
                const centerId = `scout-wp-center-${wp.sequence}`;
                const labelId = `scout-wp-label-${wp.sequence}`;

                // Circle fill
                addOrUpdateGeoJSONLayer(
                    map,
                    { sourceId: fillId, beforeId: beforeLayer },
                    {
                        id: fillId,
                        type: 'fill',
                        source: fillId,
                        layout: {},
                        paint: {
                            'fill-color': WAYPOINT_FILL_COLOR,
                            'fill-opacity': fillOpacity,
                        },
                    },
                    circle
                );
                layerIds.push(fillId);

                // Circle outline
                addOrUpdateGeoJSONLayer(
                    map,
                    { sourceId: lineId, beforeId: beforeLayer },
                    {
                        id: lineId,
                        type: 'line',
                        source: lineId,
                        layout: {},
                        paint: {
                            'line-color': WAYPOINT_LINE_COLOR,
                            'line-width': GEOFENCE_LINE_WIDTH,
                        },
                    },
                    circle
                );
                layerIds.push(lineId);

                // Waypoint center marker and label
                const waypointMarkerPoint: GeoJSON.Feature<GeoJSON.Point> = {
                    type: 'Feature',
                    properties: { label: String(wp.sequence) },
                    geometry: {
                        type: 'Point',
                        coordinates: lngLatToTurf(center),
                    },
                };

                addOrUpdateGeoJSONLayer(
                    map,
                    { sourceId: centerId, beforeId: beforeLayer },
                    {
                        id: centerId,
                        type: 'circle',
                        source: centerId,
                        paint: {
                            'circle-color': WAYPOINT_CENTER_MARKER_COLOR,
                            'circle-radius': WAYPOINT_CENTER_MARKER_RADIUS_BY_ZOOM,
                            'circle-opacity': 1,
                        },
                    } as mapboxgl.LayerSpecification,
                    waypointMarkerPoint
                );
                layerIds.push(centerId);

                addOrUpdateGeoJSONLayer(
                    map,
                    { sourceId: labelId, beforeId: beforeLayer },
                    {
                        id: labelId,
                        type: 'symbol',
                        source: labelId,
                        layout: {
                            'text-field': ['get', 'label'],
                            'text-size': WAYPOINT_LABEL_TEXT_SIZE_BY_ZOOM,
                            'text-allow-overlap': true,
                            'text-font': mapLabelFontStack,
                        },
                        paint: {
                            'text-color': WAYPOINT_LABEL_COLOR,
                        },
                    } as mapboxgl.LayerSpecification,
                    waypointMarkerPoint
                );
                layerIds.push(labelId);
            });
        }

        return () => {
            cleanupAllLayers(map, layerIds);
        };
    }, [map, isScout, scoutData, remainingWaypoints, beforeLayer, deviceLocation]);

    return null;
};
